import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, isMobileBreakpoint, startViewTransition } from '@theme/utilities';

/** When true, `filters-drawer` close must not re-morph (apply path already updated the section). */
let suppressFiltersDrawerCloseResync = false;

/**
 * Product cards after the first mobile batch are rendered inside a template.
 * Inline scripts inside Section Rendering API responses are not executed after morph,
 * so facet/sort updates must append those deferred cards explicitly.
 * @param {ParentNode} [root]
 */
function appendDeferredProductGridItems(root = document) {
  root.querySelectorAll('template[data-deferred-product-grid-items]').forEach((template) => {
    if (!(template instanceof HTMLTemplateElement)) return;

    const grid = template.previousElementSibling;
    if (!(grid instanceof HTMLElement) || !grid.matches('.product-grid')) return;
    if (!template.content?.childElementCount) {
      template.remove();
      return;
    }

    const append = () => {
      if (!template.isConnected || !template.content?.childElementCount) return;

      grid.appendChild(template.content);
      template.remove();
      grid.closest('results-list')?.updatedCallback?.();

      document.dispatchEvent(
        new CustomEvent('product-grid:deferred-items-loaded', {
          detail: { grid },
        })
      );

      if (typeof window.injectIcons === 'function') {
        window.injectIcons();
      }
    };

    if (window.matchMedia('(max-width: 749px)').matches) {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(append, { timeout: 2000 });
      } else {
        window.setTimeout(append, 0);
      }
    } else {
      append();
    }
  });
}

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';
const PRODUCTS_PER_PAGE_VIEWS = new Set(['36', '60', '90', '120']);

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Mobile PLP drawer: wait for explicit Apply instead of updating on every input change.
   * @returns {boolean}
   */
  shouldDeferFilterApply() {
    return this.dataset.deferFilterApply === 'true' && isMobileBreakpoint();
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    const sortAll = newParameters.getAll('sort_by');
    if (sortAll.length > 1) {
      const form = this.refs.facetsForm;
      const select = form instanceof HTMLFormElement ? form.querySelector('select[name="sort_by"]') : null;
      const chosen =
        select instanceof HTMLSelectElement && select.value ? select.value : sortAll[sortAll.length - 1];
      newParameters.delete('sort_by');
      newParameters.set('sort_by', chosen);
    }

    this.#collapseDuplicatePriceParams(newParameters);

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    this.#stripNoopPriceFilterParams(newParameters);

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    const currentView = new URL(window.location.href).searchParams.get('view');
    if (!newParameters.has('view') && currentView && PRODUCTS_PER_PAGE_VIEWS.has(currentView)) {
      newParameters.set('view', currentView);
    }
    newParameters.delete('limit');

    return newParameters;
  }

  /**
   * FormData → URLSearchParams can list the same price key twice (e.g. duplicate nodes / morph).
   * If any entry is empty, treat that side as cleared. Otherwise keep the last non-empty value.
   * @param {URLSearchParams} params
   */
  #collapseDuplicatePriceParams(params) {
    for (const key of ['filter.v.price.gte', 'filter.v.price.lte']) {
      const all = params.getAll(key);
      if (all.length <= 1) continue;
      params.delete(key);
      if (all.some((v) => v === '')) continue;
      const last = all[all.length - 1];
      if (last != null && last !== '') params.set(key, last);
    }
  }

  /**
   * Drop price params that mean “whole catalog” so Apply does not send filter.v.price.lte=max after Clear all.
   * @param {URLSearchParams} params
   */
  #stripNoopPriceFilterParams(params) {
    const form = this.refs.facetsForm;
    if (!(form instanceof HTMLFormElement)) return;

    const pf = form.querySelector('price-facet-component');
    if (!(pf instanceof HTMLElement)) return;

    const rangeMaxCents = Number(pf.getAttribute('data-range-max-cents') ?? '0');
    if (!Number.isFinite(rangeMaxCents) || rangeMaxCents <= 0) return;

    const gteRaw = params.get('filter.v.price.gte');
    const lteRaw = params.get('filter.v.price.lte');
    if ((gteRaw == null || gteRaw === '') && (lteRaw == null || lteRaw === '')) return;

    const minParsed = gteRaw != null && gteRaw !== '' ? parsePriceInputTextToCents(gteRaw) : null;
    const maxParsed = lteRaw != null && lteRaw !== '' ? parsePriceInputTextToCents(lteRaw) : null;

    const minCents = minParsed ?? 0;
    const maxCents = maxParsed ?? rangeMaxCents;

    if (minCents <= 0 && maxCents >= rangeMaxCents) {
      params.delete('filter.v.price.gte');
      params.delete('filter.v.price.lte');
    }
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();
    url.search = urlParameters.toString();

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = async () => {
    if (this.shouldDeferFilterApply()) return;
    await this.#performFilterUpdate();
  };

  /**
   * Mobile drawer defers facet apply, but sort must still push URL + morph immediately (same as desktop).
   */
  applySortFromFormNow = async () => {
    await this.#performFilterUpdate();
  };

  /**
   * Push URL from form, notify listeners, morph the collection section.
   */
  async #performFilterUpdate() {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    await this.#updateSection();
  }

  /**
   * Mobile drawer "Anwenden": apply pending facet state, then close the filter dialog.
   */
  applyFiltersAndCloseDrawer = async () => {
    const drawer = document.getElementById('filters-drawer');
    try {
      if (this.shouldDeferFilterApply()) {
        suppressFiltersDrawerCloseResync = true;
        await this.#performFilterUpdate();
      }
      if (drawer instanceof DialogComponent) {
        await drawer.closeDialog(false);
      }
    } finally {
      suppressFiltersDrawerCloseResync = false;
    }
  };

  async #updateSection() {
    document.querySelectorAll('.facets__search-input').forEach((input) => {
      input.value = '';
    });

    await sectionRenderer.renderSection(this.sectionId, { cache: false });
    appendDeferredProductGridItems();
    document.dispatchEvent(new CustomEvent('filter:update-complete', { bubbles: true }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
    scheduleFacetsDrawerMobileMasterDetail();
  }

  /**
   * Search params for a navigated URL (e.g. clear-all). Must not use the form yet — inputs still reflect the old state.
   * @param {string | URL} href
   * @returns {URLSearchParams}
   */
  #searchParamsFromNavigatedUrl(href) {
    const resolved = href instanceof URL ? href : new URL(href, window.location.origin);
    const params = new URLSearchParams(resolved.search);

    if (params.get('filter.v.price.gte') === '') params.delete('filter.v.price.gte');
    if (params.get('filter.v.price.lte') === '') params.delete('filter.v.price.lte');
    params.delete('page');

    const searchQuery = resolved.searchParams.get(SEARCH_QUERY) ?? '';
    if (searchQuery) params.set(SEARCH_QUERY, searchQuery);

    const currentView = new URL(window.location.href).searchParams.get('view');
    if (!params.has('view') && currentView && PRODUCTS_PER_PAGE_VIEWS.has(currentView)) {
      params.set('view', currentView);
    }
    params.delete('limit');

    const sortVals = params.getAll('sort_by');
    if (sortVals.length > 1) {
      params.delete('sort_by');
      params.set('sort_by', sortVals[sortVals.length - 1]);
    }

    return params;
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  async updateFiltersByURL(url) {
    const resolved = new URL(url, window.location.origin);
    history.pushState('', '', resolved.toString());

    const params = this.#searchParamsFromNavigatedUrl(resolved);
    this.dispatchEvent(new FilterUpdateEvent(params));
    await this.#updateSection();
  }

  /**
   * Mobile drawer + deferred apply: clear all filter inputs and summaries locally so "Alles löschen"
   * resets checkbox UI even when the URL was already unfiltered (pending selections only).
   */
  clearDeferredDrawerFilterUi() {
    const form = this.refs.facetsForm;
    if (!(form instanceof HTMLFormElement)) return;

    form.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = false;
    });
    form.querySelectorAll('input[type="radio"][name^="filter."]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = false;
    });

    form.querySelectorAll('facet-inputs-component').forEach((el) => {
      if (el instanceof FacetInputsComponent) {
        el.refreshSummaryFromDom();
      }
    });

    // Reset sliders / presets first; then force-clear every gte/lte in the form so sync cannot leave a stale lte (e.g. 199).
    form.querySelectorAll('price-facet-component').forEach((el) => {
      if (el instanceof PriceFacetComponent) {
        el.resetLocalPriceUi();
      }
    });

    form.querySelectorAll('input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = '';
    });

    // Preis row: always wipe summary span after reset (avoids stale range in tab / accordion header).
    form.querySelectorAll('price-facet-component').forEach((el) => {
      const details = el.closest('details');
      const st = details?.querySelector('summary facet-status-component');
      if (st instanceof FacetStatusComponent) st.clearSummary();
    });

    syncDeferredDrawerFilterCountUi(this);
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
    if (facetsForm.shouldDeferFilterApply()) {
      syncDeferredDrawerFilterCountUi(facetsForm);
    }
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }

  /** Re-read checked facet inputs and refresh the accordion status (no network). */
  refreshSummaryFromDom() {
    this.#updateSelectedFacetSummary();
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLInputElement} [rangeMinInput] - Lower range thumb (cents)
 * @property {HTMLInputElement} [rangeMaxInput] - Upper range thumb (cents)
 * @property {HTMLElement} [rangeFill] - Selected span between thumbs
 */

/**
 * Handles price facet functionality (text inputs, dual range slider, preset bands).
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {(() => void) | undefined} */
  #teardownUi;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.#bindUi();
    requestAnimationFrame(() => {
      this.#syncSlidersFromTextInputs();
      this.#updateRangeFillVisual();
      this.#syncPresetCheckboxesFromText();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#teardownUi?.();
    this.#teardownUi = undefined;
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  #bindUi() {
    this.#teardownUi?.();
    const offs = [];
    const { minInput, maxInput, rangeMinInput, rangeMaxInput } = this.refs;

    const onTextChange = () => {
      this.#clearPresetCheckboxes();
      this.updatePriceFilterAndResults();
    };

    minInput?.addEventListener('change', onTextChange);
    maxInput?.addEventListener('change', onTextChange);
    offs.push(() => {
      minInput?.removeEventListener('change', onTextChange);
      maxInput?.removeEventListener('change', onTextChange);
    });

    const onRangeInput = () => {
      this.#constrainSliderThumbs();
      this.#syncTextInputsFromSliders();
      this.#updateRangeFillVisual();
    };

    const onRangeChange = () => {
      this.#clearPresetCheckboxes();
      this.#constrainSliderThumbs();
      this.#syncTextInputsFromSliders();
      this.#updateRangeFillVisual();
      this.updatePriceFilterAndResults();
    };

    rangeMinInput?.addEventListener('input', onRangeInput);
    rangeMaxInput?.addEventListener('input', onRangeInput);
    rangeMinInput?.addEventListener('change', onRangeChange);
    rangeMaxInput?.addEventListener('change', onRangeChange);
    offs.push(() => {
      rangeMinInput?.removeEventListener('input', onRangeInput);
      rangeMaxInput?.removeEventListener('input', onRangeInput);
      rangeMinInput?.removeEventListener('change', onRangeChange);
      rangeMaxInput?.removeEventListener('change', onRangeChange);
    });

    this.querySelectorAll('.price-facet__preset-input').forEach((el) => {
      el.addEventListener('change', this.#onPresetChange);
      offs.push(() => el.removeEventListener('change', this.#onPresetChange));
    });

    this.#teardownUi = () => offs.forEach((fn) => fn());
  }

  /**
   * @param {Event} event
   */
  #onPresetChange = (event) => {
    const el = event.target;
    if (!(el instanceof HTMLInputElement) || !el.classList.contains('price-facet__preset-input')) return;

    if (el.checked) {
      this.querySelectorAll('.price-facet__preset-input').forEach((o) => {
        if (o !== el && o instanceof HTMLInputElement) o.checked = false;
      });
      const lo = Number(el.dataset.minCents ?? 0);
      const hi = Number(el.dataset.maxCents ?? 0);
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return;
      this.#setTextInputsFromCents(lo, hi);
      this.#syncSlidersFromTextInputs();
      this.#updateRangeFillVisual();
      this.updatePriceFilterAndResults();
      return;
    }

    const { minInput, maxInput } = this.refs;
    if (minInput instanceof HTMLInputElement) minInput.value = '';
    if (maxInput instanceof HTMLInputElement) maxInput.value = '';
    this.#syncSlidersFromTextInputs();
    this.#updateRangeFillVisual();
    this.updatePriceFilterAndResults();
  };

  #getRangeMaxCents() {
    const n = Number(this.dataset.rangeMaxCents ?? 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  #constrainSliderThumbs() {
    const { rangeMinInput, rangeMaxInput } = this.refs;
    if (!(rangeMinInput instanceof HTMLInputElement) || !(rangeMaxInput instanceof HTMLInputElement)) return;
    let min = Number(rangeMinInput.value);
    let max = Number(rangeMaxInput.value);
    if (!Number.isFinite(min)) min = 0;
    if (!Number.isFinite(max)) max = 100;
    min = Math.max(0, Math.min(min, 100));
    max = Math.max(0, Math.min(max, 100));
    if (min > max) {
      const t = min;
      min = max;
      max = t;
    }
    rangeMinInput.value = String(min);
    rangeMaxInput.value = String(max);
  }

  #syncTextInputsFromSliders() {
    const { rangeMinInput, rangeMaxInput } = this.refs;
    if (!(rangeMinInput instanceof HTMLInputElement) || !(rangeMaxInput instanceof HTMLInputElement)) return;
    const min = this.#percentToCents(Number(rangeMinInput.value));
    const max = this.#percentToCents(Number(rangeMaxInput.value));
    this.#setTextInputsFromCents(min, max);
  }

  /**
   * @param {number} minCents
   * @param {number} maxCents
   */
  #setTextInputsFromCents(minCents, maxCents) {
    const { minInput, maxInput } = this.refs;
    const maxRange = this.#getRangeMaxCents();
    const locale = this.dataset.locale || undefined;
    const fmt = (cents) =>
      (cents / 100).toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

    if (minInput instanceof HTMLInputElement) {
      minInput.value = minCents <= 0 ? '' : fmt(minCents);
    }
    if (maxInput instanceof HTMLInputElement) {
      maxInput.value = maxCents >= maxRange ? '' : fmt(maxCents);
    }
  }

  /**
   * Clear price fields, presets, and range visuals without submitting filters (deferred drawer "clear all").
   */
  resetLocalPriceUi() {
    const { minInput, maxInput, rangeMinInput, rangeMaxInput } = this.refs;
    if (minInput instanceof HTMLInputElement) minInput.value = '';
    if (maxInput instanceof HTMLInputElement) maxInput.value = '';
    this.querySelectorAll('.price-facet__preset-input').forEach((o) => {
      if (o instanceof HTMLInputElement) o.checked = false;
    });
    if (rangeMinInput instanceof HTMLInputElement) rangeMinInput.value = '0';
    if (rangeMaxInput instanceof HTMLInputElement) rangeMaxInput.value = '100';
    this.#syncTextInputsFromSliders();
    this.#updateRangeFillVisual();
    this.#syncPresetCheckboxesFromText();
    this.#updateSummary();
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');
    if (statusComponent instanceof FacetStatusComponent) {
      const minV = (minInput instanceof HTMLInputElement ? minInput.value : '').trim();
      const maxV = (maxInput instanceof HTMLInputElement ? maxInput.value : '').trim();
      if (!minV && !maxV) statusComponent.clearSummary();
    }
  }

  #syncSlidersFromTextInputs() {
    const { rangeMinInput, rangeMaxInput, minInput, maxInput } = this.refs;
    if (!(rangeMinInput instanceof HTMLInputElement) || !(rangeMaxInput instanceof HTMLInputElement)) return;
    const rangeMax = this.#getRangeMaxCents();
    if (rangeMax <= 0) return;

    const minParsed = this.#parseMoneyTextToCents(minInput?.value ?? '');
    const maxParsed = this.#parseMoneyTextToCents(maxInput?.value ?? '');

    const minCents = minParsed ?? 0;
    const maxEffective = maxParsed ?? rangeMax;

    let lo = Math.max(0, Math.min(minCents, rangeMax));
    let hi = Math.max(0, Math.min(maxEffective, rangeMax));
    if (lo > hi) [lo, hi] = [hi, lo];

    // Map real cents to 0-100 scale for non-linear slider
    rangeMinInput.value = String(this.#centsToPercent(lo));
    rangeMaxInput.value = String(this.#centsToPercent(hi));
  }

  #centsToPercent(cents) {
    const rangeMax = this.#getRangeMaxCents();
    if (rangeMax <= 0 || cents <= 0) return 0;
    if (cents >= rangeMax) return 100;
    return (cents / rangeMax) * 100;
  }

  #percentToCents(percent) {
    const rangeMax = this.#getRangeMaxCents();
    if (rangeMax <= 0 || percent <= 0) return 0;
    if (percent >= 100) return rangeMax;
    return Math.round((percent / 100) * rangeMax);
  }

  /**
   * @param {string} text
   * @returns {number | null}
   */
  #parseMoneyTextToCents(text) {
    const t = text.trim();
    if (!t) return null;

    // Use a more robust normalization: 
    // If there's a dot and a comma, the comma is likely the decimal.
    // If there's only a dot and it looks like a thousands separator (e.g. 1.000), strip it.
    let val = t.replace(/[^\d.,]/g, '');
    
    // formatMoney in utilities.js handles some cases, but let's be explicit
    // for common "1.000" -> 1000 cases where it might fail.
    if (val.includes('.') && !val.includes(',')) {
      const parts = val.split('.');
      if (parts.length > 1 && parts[parts.length - 1].length !== 2) {
        // Not a typical decimal part like .99 or .00? Probably thousands.
        // Actually, let's just use the theme's formatMoney and fix it there if needed, 
        // but for now, let's handle the simple case.
        if (parts[parts.length - 1].length === 3) val = val.replace(/\./g, '');
      }
    }

    const norm = formatMoney(val);
    const n = Number(norm);
    if (Number.isNaN(n)) return null;
    return Math.round(n * 100);
  }

  #updateRangeFillVisual() {
    const fill = this.refs.rangeFill || this.querySelector('[ref="rangeFill"]');
    if (!(fill instanceof HTMLElement)) return;

    const rangeMinInput = this.refs.rangeMinInput || this.querySelector('[ref="rangeMinInput"]');
    const rangeMaxInput = this.refs.rangeMaxInput || this.querySelector('[ref="rangeMaxInput"]');

    if (!(rangeMinInput instanceof HTMLInputElement) || !(rangeMaxInput instanceof HTMLInputElement)) return;

    const p1 = parseFloat(rangeMinInput.value);
    const p2 = parseFloat(rangeMaxInput.value);

    const left = Math.min(p1, p2);
    const right = Math.max(p1, p2);

    fill.style.setProperty('left', `${left}%`, 'important');
    fill.style.setProperty('width', `${right - left}%`, 'important');

    // Update labels
    const minLabel = this.refs.rangeMinLabel || this.querySelector('[ref="rangeMinLabel"]');
    const maxLabel = this.refs.rangeMaxLabel || this.querySelector('[ref="rangeMaxLabel"]');

    if (minLabel instanceof HTMLElement) {
      minLabel.style.left = `${p1}%`;
      const cents = this.#percentToCents(p1);
      if (p1 <= 0.1) {
        minLabel.textContent = '0';
      } else {
        minLabel.textContent = `${Math.round(cents / 100)}€`;
      }
    }
    if (maxLabel instanceof HTMLElement) {
      maxLabel.style.left = `${p2}%`;
      const cents = this.#percentToCents(p2);
      if (p2 >= 99.9) {
        maxLabel.textContent = '+2000€';
      } else {
        maxLabel.textContent = `${Math.round(cents / 100)}€`;
      }
    }
  }

  #clearPresetCheckboxes() {
    this.querySelectorAll('.price-facet__preset-input').forEach((el) => {
      if (el instanceof HTMLInputElement) el.checked = false;
    });
  }

  #syncPresetCheckboxesFromText() {
    const minRaw = this.refs.minInput?.value ?? '';
    const maxRaw = this.refs.maxInput?.value ?? '';
    const minEmpty = !String(minRaw).trim();
    const maxEmpty = !String(maxRaw).trim();
    // Empty fields mean “no explicit range” (full-range UI uses blanks). Do not treat as 0..range_max
    // or a preset band can spuriously match.
    if (minEmpty && maxEmpty) {
      this.#clearPresetCheckboxes();
      return;
    }

    const minCents = this.#parseMoneyTextToCents(minRaw);
    const maxCents = this.#parseMoneyTextToCents(maxRaw);
    const maxRange = this.#getRangeMaxCents();
    this.querySelectorAll('.price-facet__preset-input').forEach((el) => {
      if (!(el instanceof HTMLInputElement)) return;
      const lo = Number(el.dataset.minCents);
      const hi = Number(el.dataset.maxCents);
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return;
      const tMin = minCents ?? 0;
      const tMax = maxCents ?? maxRange;
      el.checked = tMin === lo && tMax === hi;
    });
  }

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
    this.#syncSlidersFromTextInputs();
    this.#updateRangeFillVisual();
    this.#syncPresetCheckboxesFromText();
    if (facetsForm.shouldDeferFilterApply()) {
      syncDeferredDrawerFilterCountUi(facetsForm);
    }
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The text input element to adjust
   */
  #adjustToValidValues(input) {
    if (!(input instanceof HTMLInputElement)) return;
    if (input.value.trim() === '') return;

    const value = Number(formatMoney(input.value.trim()));
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (Number.isNaN(value)) return;
    if (!Number.isNaN(min) && value < min) input.value = input.getAttribute('data-min') ?? '';
    if (!Number.isNaN(max) && value > max) input.value = input.getAttribute('data-max') ?? '';
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    if (facetsForm.shouldDeferFilterApply()) {
      syncDeferredDrawerFilterCountUi(facetsForm);
    }
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  async removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const rawTarget = event && 'target' in event ? event.target : null;
    const targetEl = rawTarget instanceof HTMLElement ? rawTarget : null;
    const clearAllBtn = targetEl?.closest('button.facets__clear-all');
    const isMobileDrawerClearAll =
      facetsForm.shouldDeferFilterApply() &&
      !!clearAllBtn?.closest('.facets__actions--drawer-mobile-footer');

    // Mobile drawer "Alles löschen": reset UI only — URL/products update on "Anwenden".
    if (isMobileDrawerClearAll) {
      facetsForm.clearDeferredDrawerFilterUi();
      facetsForm.dispatchEvent(new FilterUpdateEvent(new URLSearchParams()));
      return;
    }

    await facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    // Update displayed value immediately so the dropdown shows the selected option
    this.updateSortDisplay(event);

    const runUpdate = () => {
      if (facetsForm.shouldDeferFilterApply()) {
        return facetsForm.applySortFromFormNow();
      }
      return facetsForm.updateFilters();
    };

    void Promise.resolve(runUpdate()).finally(() => {
      if (shouldDisable) {
        if (isMobile) {
          const inputs = this.querySelectorAll('input[name="sort_by"]');
          inputs.forEach((input) => {
            if (!(input instanceof HTMLInputElement)) return;
            input.disabled = false;
          });
        } else {
          const selectElement = this.querySelector('select[name="sort_by"]');
          if (selectElement instanceof HTMLSelectElement) {
            selectElement.disabled = false;
          }
        }
      }
    });

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the displayed sort option label so it always reflects the selected option.
   * Works for both desktop (radio) and mobile (select). Only the selected value text updates; the label stays static.
   */
  updateSortDisplay(event) {
    const details = this.refs?.details ?? this.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) return;

    let selectedLabel = '';
    const target = event?.target;

    if (target instanceof HTMLSelectElement) {
      const option = target.options[target.selectedIndex];
      selectedLabel = option ? option.textContent.trim() : '';
    } else if (target instanceof HTMLInputElement && target.name === 'sort_by' && target.type === 'radio') {
      const optionEl = target.closest('[role="option"]');
      const labelEl = optionEl?.querySelector('.sorting-filter__label');
      selectedLabel = labelEl ? labelEl.textContent.trim() : '';
    }

    // Fallback: read from currently checked/selected input (e.g. if event.target was label or event was synthetic)
    if (!selectedLabel) {
      const checkedRadio = this.querySelector('input[name="sort_by"][type="radio"]:checked');
      if (checkedRadio instanceof HTMLInputElement) {
        const optionEl = checkedRadio.closest('[role="option"]');
        const labelEl = optionEl?.querySelector('.sorting-filter__label');
        selectedLabel = labelEl ? labelEl.textContent.trim() : '';
      }
    }
    if (!selectedLabel) {
      const selectEl = this.querySelector('select[name="sort_by"]');
      if (selectEl instanceof HTMLSelectElement) {
        const option = selectEl.options[selectEl.selectedIndex];
        selectedLabel = option ? option.textContent.trim() : '';
      }
    }

    const valueSpan = this.refs?.sortSelectedValue ?? this.querySelector('.sorting-filter__selected-value');
    const mobileSpan = this.refs?.sortSelectedValueMobile ?? this.querySelector('.facets__status.h6');
    if (valueSpan && valueSpan instanceof HTMLElement) valueSpan.textContent = selectedLabel;
    if (mobileSpan && mobileSpan instanceof HTMLElement) mobileSpan.textContent = selectedLabel;
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    facetStatus.innerHTML = `(${checkedInputElementsCount})`;
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = (minInput instanceof HTMLInputElement ? minInput.value : '').trim();
    const maxInputValue = (maxInput instanceof HTMLInputElement ? maxInput.value : '').trim();
    const { facetStatus } = this.refs;
    if (!(facetStatus instanceof HTMLElement)) return;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

/**
 * Facet value search: shows/hides `.facets__list-item` rows as the user types in `.facets__search-input`.
 * Markup is output from `list-filter.liquid` with `data-filter-list-id` matching the `<ul id>` (unique per main vs drawer).
 * Runs on any page where these inputs exist; inputs are only rendered for colour + category filters in `list-filter`.
 */

/**
 * @param {string | null | undefined} raw
 * @returns {string[]}
 */
function facetSearchCollapsedTokens(raw) {
  if (!raw) return [];
  return raw.trim().split(/\s+/).filter(Boolean);
}

/**
 * Searchable text for a row: prefer `data-facet-value-label` (swatches often hide label text from `textContent`).
 * @param {HTMLElement} item
 * @returns {string}
 */
function facetSearchRowText(item) {
  const fromAttr = item.getAttribute('data-facet-value-label');
  if (fromAttr) return fromAttr.trim().toLowerCase();
  return (item.textContent ?? '').trim().toLowerCase();
}

/**
 * @param {HTMLInputElement} input
 */
function applyFacetsSearchInput(input) {
  if (!(input instanceof HTMLInputElement)) return;

  const listId = input.getAttribute('data-filter-list-id');
  if (!listId) return;

  const root =
    input.closest('facet-inputs-component') ??
    input.closest('.facets__inputs') ??
    input.closest('.facets__panel-content') ??
    input.closest('details.facets__panel');

  let list =
    root instanceof HTMLElement ? root.querySelector(`#${CSS.escape(listId)}`) : null;
  if (!(list instanceof HTMLElement)) {
    list = document.getElementById(listId);
  }
  if (!(list instanceof HTMLElement)) return;

  const value = input.value.trim().toLowerCase();
  const items = list.querySelectorAll('.facets__list-item');
  const showMore = root.querySelector('show-more-component');
  const showMoreExpanded = showMore instanceof HTMLElement && showMore.dataset.expanded === 'true';
  const showMoreButton = showMore instanceof HTMLElement ? showMore.querySelector('[ref="showMoreButton"]') : null;

  if (showMoreButton instanceof HTMLElement) {
    showMoreButton.style.display = value ? 'none' : '';
  }

  for (const item of items) {
    if (!(item instanceof HTMLElement)) continue;
    const text = facetSearchRowText(item);

    if (!value) {
      item.style.removeProperty('display');
      const collapsedByShowMore = item.getAttribute('data-facet-collapsed-by-show-more') === 'true';
      const shouldBeHidden = collapsedByShowMore && !showMoreExpanded;

      if (shouldBeHidden) {
        const tokens = facetSearchCollapsedTokens(item.getAttribute('data-facet-collapsed-classes'));
        item.classList.add(...tokens);
      } else {
        item.classList.remove('hidden', 'mobile:hidden');
      }
      continue;
    }

    if (text.includes(value)) {
      item.style.removeProperty('display');
      item.classList.remove('hidden', 'mobile:hidden');
    } else {
      item.style.setProperty('display', 'none', 'important');
    }
  }
}

function facetSearchInputTarget(event) {
  const node = event.composedPath?.()[0] ?? event.target;
  if (!(node instanceof HTMLInputElement)) return null;
  if (!node.closest('.facets')) return null;
  if (node.classList.contains('facets__search-input')) return node;
  if (node.type === 'search' && node.hasAttribute('data-filter-list-id')) return node;
  return null;
}

document.addEventListener(
  'input',
  (event) => {
    const input = facetSearchInputTarget(event);
    if (!input) return;
    applyFacetsSearchInput(input);
  },
  true
);

/** `type="search"` fires `search` when the field is cleared via the browser UI */
document.addEventListener(
  'search',
  (event) => {
    const input = facetSearchInputTarget(event);
    if (!input) return;
    applyFacetsSearchInput(input);
  },
  true
);

document.addEventListener(
  'keydown',
  (event) => {
    const input = facetSearchInputTarget(event);
    if (!input) return;
    if (event.key === 'Enter') event.preventDefault();
  },
  true
);

/**
 * Collection PLP mobile sticky bar: open sort UI (drawer + sorting accordion, or grid fallback).
 */
function plpStickyRevealBar() {
  updatePlpMobileStickyBarPaginationBoundary();

  const bar = document.querySelector('[data-plp-mobile-sticky-actions]');
  if (!(bar instanceof HTMLElement)) return;
  if (bar.classList.contains('plp-mobile-sticky-actions--past-pagination')) return;

  bar.classList.remove('plp-mobile-sticky-actions--hidden-on-scroll');
}

/** Debounce id for “scroll stopped” on PLP mobile sticky bar */
let plpStickyScrollStopTimer = 0;

/**
 * Collection PLP (mobile): hide sticky sort/filter bar once the pagination area reaches it.
 */
function updatePlpMobileStickyBarPaginationBoundary() {
  const bar = document.querySelector('[data-plp-mobile-sticky-actions]');
  if (!(bar instanceof HTMLElement)) return;

  if (!isMobileBreakpoint()) {
    bar.classList.remove('plp-mobile-sticky-actions--past-pagination');
    return;
  }

  const scope = bar.closest('main') ?? document;
  const pagination = scope.querySelector(
    '.pagination-full-width-wrapper, .pagination-figma-container, nav.pagination, .pagination',
  );

  if (!(pagination instanceof HTMLElement)) {
    bar.classList.remove('plp-mobile-sticky-actions--past-pagination');
    return;
  }

  const barHeight = bar.offsetHeight || 52;
  const paginationTop = pagination.getBoundingClientRect().top;
  const stickyTop = window.innerHeight - barHeight;

  bar.classList.toggle('plp-mobile-sticky-actions--past-pagination', paginationTop <= stickyTop);
}

/**
 * Collection PLP (mobile): hide sticky sort/filter bar while scrolling; show again after scroll stops.
 */
function initPlpMobileStickyBarScrollHide() {
  const SCROLL_STOP_MS = 160;

  const onScroll = () => {
    if (!isMobileBreakpoint()) return;
    updatePlpMobileStickyBarPaginationBoundary();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const bar = document.querySelector('[data-plp-mobile-sticky-actions]');
    if (!(bar instanceof HTMLElement)) return;
    if (bar.classList.contains('plp-mobile-sticky-actions--past-pagination')) return;

    bar.classList.add('plp-mobile-sticky-actions--hidden-on-scroll');
    window.clearTimeout(plpStickyScrollStopTimer);
    plpStickyScrollStopTimer = window.setTimeout(() => {
      plpStickyRevealBar();
    }, SCROLL_STOP_MS);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', updatePlpMobileStickyBarPaginationBoundary, { passive: true });

  const resultsList = document.querySelector('results-list');
  if (resultsList instanceof HTMLElement) {
    resultsList.addEventListener('scroll', onScroll, { passive: true });
  }

  updatePlpMobileStickyBarPaginationBoundary();
}

/**
 * Mobile PLP: slide sticky bar down while sort sheet / filter drawer is open; slide back up on close.
 * @param {boolean} open
 */
function plpStickyBarSetSheetOpen(open) {
  const bar = document.querySelector('[data-plp-mobile-sticky-actions]');
  if (!(bar instanceof HTMLElement)) return;
  bar.classList.toggle('plp-mobile-sticky-actions--sheet-open', Boolean(open));
}

function plpStickyOpenSortPanel() {
  plpStickyRevealBar();

  const sortSheet = document.getElementById('sort-sheet-mobile');
  if (sortSheet && typeof sortSheet.showDialog === 'function') {
    sortSheet.showDialog();
    return;
  }

  const drawerComponent = document.querySelector('#filters-drawer');
  const sortDetailsInDrawer = document.querySelector('#filters-drawer sorting-filter-component details');

  const openDetailsInDrawer = () => {
    const details = document.querySelector('#filters-drawer sorting-filter-component details');
    const summary = details?.querySelector('summary');
    if (!(details instanceof HTMLDetailsElement) || !summary) return;
    if (!details.open) summary.click();
    details.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  if (sortDetailsInDrawer instanceof HTMLDetailsElement && drawerComponent && 'showDialog' in drawerComponent) {
    /** @type {{ showDialog?: () => void }} */ (drawerComponent).showDialog?.();
    requestAnimationFrame(() => {
      requestAnimationFrame(openDetailsInDrawer);
    });
    return;
  }

  document.querySelector('.product-grid-top-bar sorting-filter-component summary')?.click();
}

/**
 * PLP mobile sort sheet: apply sort via the same controls as the filter drawer (no duplicate logic).
 * On mobile the drawer uses <select name="sort_by">; radios are hidden — FormData / URL must follow the select.
 */
function plpSortSheetApplyValue(value) {
  const drawerSorting = document.querySelector('#filters-drawer sorting-filter-component');
  if (!drawerSorting) return false;

  const select = drawerSorting.querySelector('select[name="sort_by"]');
  if (select instanceof HTMLSelectElement && !select.disabled) {
    const optionExists = Array.from(select.options).some((opt) => opt.value === value);
    if (optionExists) {
      select.value = value;
      if (typeof drawerSorting.updateFilterAndSorting === 'function') {
        drawerSorting.updateFilterAndSorting(/** @type {any} */ ({ target: select }));
        return true;
      }
    }
  }

  const radio = drawerSorting.querySelector(
    `input[name="sort_by"][type="radio"][value="${CSS.escape(value)}"]`
  );
  if (!(radio instanceof HTMLInputElement)) return false;

  radio.checked = true;
  const selectSync = drawerSorting.querySelector('select[name="sort_by"]');
  if (selectSync instanceof HTMLSelectElement) {
    selectSync.value = value;
  }
  if (typeof drawerSorting.updateFilterAndSorting === 'function') {
    const evtTarget =
      selectSync instanceof HTMLSelectElement ? selectSync : radio;
    drawerSorting.updateFilterAndSorting(/** @type {any} */ ({ target: evtTarget }));
    return true;
  }

  radio.click();
  return true;
}

function plpSortSheetClose() {
  const sortSheet = document.getElementById('sort-sheet-mobile');
  if (sortSheet && typeof sortSheet.closeDialog === 'function') {
    void sortSheet.closeDialog();
  }
}

function plpSortSheetUpdateSelection(selectedButton) {
  const sheet = document.getElementById('sort-sheet-mobile');
  if (!sheet) return;
  sheet.querySelectorAll('.sort-sheet-mobile__option').forEach((btn) => {
    if (!(btn instanceof HTMLButtonElement)) return;
    btn.classList.remove('sort-sheet-mobile__option--selected');
    btn.setAttribute('aria-selected', 'false');
  });
  selectedButton.classList.add('sort-sheet-mobile__option--selected');
  selectedButton.setAttribute('aria-selected', 'true');
}

document.addEventListener('click', (event) => {
  const option = event.target.closest('.sort-sheet-mobile__option');
  const sheet = document.getElementById('sort-sheet-mobile');
  if (!(option instanceof HTMLButtonElement) || !sheet?.contains(option)) return;

  const value = option.getAttribute('data-sort-value');
  if (!value) return;

  event.preventDefault();
  plpSortSheetUpdateSelection(option);
  if (plpSortSheetApplyValue(value)) {
    plpSortSheetClose();
  }
});

document.addEventListener('click', (event) => {
  const sortTrigger = event.target.closest('[data-plp-sticky-sort]');
  if (!(sortTrigger instanceof HTMLElement)) return;
  event.preventDefault();
  plpStickyOpenSortPanel();
});

/**
 * PLP mobile sticky Filtern: same as #filters-drawer/showDialog (imperative so taps always reach the drawer).
 */
function plpStickyOpenFiltersDrawer() {
  plpStickyRevealBar();
  const drawer = document.getElementById('filters-drawer');
  if (drawer && typeof drawer.showDialog === 'function') {
    drawer.showDialog();
  }
}

document.addEventListener('click', (event) => {
  const filterTrigger = event.target.closest('[data-plp-sticky-filters]');
  if (!(filterTrigger instanceof HTMLElement)) return;
  event.preventDefault();
  plpStickyOpenFiltersDrawer();
});

/**
 * Presentational label for mobile facet tabs (consistent word casing from Shopify filter labels).
 * @param {string} raw
 */
function formatFacetDrawerTabLabel(raw) {
  const s = (raw || '').trim();
  if (!s) return raw || '';
  return s
    .split(/\s+/)
    .map((part) => {
      if (!part) return part;
      const lower = part.toLocaleLowerCase(undefined);
      return lower.charAt(0).toLocaleUpperCase(undefined) + lower.slice(1);
    })
    .join(' ');
}

/**
 * Parse storefront price text to cents (same rules as {@link PriceFacetComponent}).
 * @param {string} text
 * @returns {number | null}
 */
function parsePriceInputTextToCents(text) {
  const t = (text || '').trim();
  if (!t) return null;
  const norm = formatMoney(t);
  const n = Number(norm);
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100);
}

/**
 * True when a checked price preset is a real band (not 0..catalog max — that is not a filter).
 * @param {Element} pf
 * @returns {boolean}
 */
function priceFacetHasMeaningfulPresetSelection(pf) {
  if (!(pf instanceof Element)) return false;

  const rangeMaxCents = Number(pf.getAttribute('data-range-max-cents') ?? '0');
  if (!Number.isFinite(rangeMaxCents) || rangeMaxCents <= 0) return false;

  for (const el of pf.querySelectorAll('.price-facet__preset-input:checked')) {
    if (!(el instanceof HTMLInputElement) || el.disabled) continue;
    const lo = Number(el.dataset.minCents ?? '');
    const hi = Number(el.dataset.maxCents ?? '');
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return true;
    if (lo <= 0 && hi >= rangeMaxCents) continue;
    return true;
  }
  return false;
}

/**
 * True when the price facet should count as an applied / pending filter (not full catalog range).
 * @param {Element} pf
 */
function priceFacetHasNonDefaultRange(pf) {
  if (!(pf instanceof Element)) return false;

  if (priceFacetHasMeaningfulPresetSelection(pf)) return true;

  const min = pf.querySelector('input[name="filter.v.price.gte"]');
  const max = pf.querySelector('input[name="filter.v.price.lte"]');
  const minStr = min instanceof HTMLInputElement ? min.value.trim() : '';
  const maxStr = max instanceof HTMLInputElement ? max.value.trim() : '';
  if (!minStr && !maxStr) return false;

  const rangeMaxCents = Number(pf.getAttribute('data-range-max-cents') ?? '0');
  if (!Number.isFinite(rangeMaxCents) || rangeMaxCents <= 0) return true;

  const minParsed = parsePriceInputTextToCents(minStr);
  const maxParsed = parsePriceInputTextToCents(maxStr);
  const minCents = minParsed ?? 0;
  const maxCents = maxParsed ?? rangeMaxCents;
  if (minCents > maxCents) return true;

  const isFullCatalogRange = minCents <= 0 && maxCents >= rangeMaxCents;
  return !isFullCatalogRange;
}

/**
 * Mobile master-detail left nav: price is single-select — show "(1)" only when a real constraint exists.
 * @param {Element} priceRoot
 * @returns {string}
 */
function priceFacetMobileMasterDetailTabSuffix(priceRoot) {
  if (!priceFacetHasNonDefaultRange(priceRoot)) return '';
  return ' (1)';
}

/**
 * Pending selections in deferred drawer (aligns roughly with Liquid total_active_values).
 * @param {HTMLFormElement} form
 */
function countPendingFilterSelections(form) {
  let n = form.querySelectorAll('input[type="checkbox"][name^="filter."]:checked').length;

  form.querySelectorAll('input[type="radio"][name^="filter."]:checked').forEach(() => {
    n += 1;
  });

  form.querySelectorAll('price-facet-component').forEach((pf) => {
    if (priceFacetHasNonDefaultRange(pf)) n += 1;
  });

  return n;
}

/** Re-read each facet's status from the DOM and refresh left nav tab labels (deferred apply). */
function syncFacetsDrawerMobileMasterDetailTabLabels() {
  const mq = window.matchMedia('(max-width: 749px)');
  if (!mq.matches) return;

  const root = document.querySelector('#filters-drawer [data-facets-mobile-drawer-masterdetail]');
  if (!root) return;

  const nav = root.querySelector('.facets-mobile-drawer-masterdetail__nav');
  const slots = Array.from(root.querySelectorAll('.facets-mobile-drawer-masterdetail__panel-slot'));
  if (!(nav instanceof HTMLElement) || slots.length === 0) return;

  slots.forEach((slot, index) => {
    const summary = slot.querySelector('summary.facets__summary');
    const btn = nav.children[index];
    if (!(summary instanceof HTMLElement) || !(btn instanceof HTMLButtonElement)) return;

    const labelEl = summary.querySelector('.facets__label');
    const title = labelEl?.textContent?.trim() || `Filter ${index + 1}`;
    let suffix = '';
    const status = summary.querySelector('.facets__status');
    const priceRoot = slot.querySelector('price-facet-component');
    if (priceRoot) {
      suffix = priceFacetMobileMasterDetailTabSuffix(priceRoot);
    } else if (status) {
      const s = status.textContent?.replace(/\s+/g, ' ').trim();
      if (s) suffix = s.startsWith('(') ? ` ${s}` : ` ${s}`;
    }
    const displayTitle = formatFacetDrawerTabLabel(title);
    if (suffix) {
      btn.innerHTML = `${displayTitle} <span class="facets-mobile-drawer-masterdetail__tab-count">${suffix}</span>`;
    } else {
      btn.textContent = displayTitle;
    }
  });
}

function updateStickyFilterCount(count) {
  const label = document.querySelector('.plp-mobile-sticky-actions__label');
  const wrap = label?.parentElement;
  if (!(wrap instanceof HTMLElement)) return;

  let el = document.querySelector('[data-plp-sticky-filter-count]');
  const btn = document.querySelector('[data-plp-sticky-filters]');

  if (count <= 0) {
    el?.remove();
    if (btn instanceof HTMLElement) {
      const t = label instanceof HTMLElement ? label.textContent?.trim() || '' : '';
      btn.setAttribute('aria-label', t || btn.getAttribute('aria-label') || '');
    }
    return;
  }

  if (!el) {
    el = document.createElement('span');
    el.className = 'plp-mobile-sticky-actions__count';
    el.setAttribute('data-plp-sticky-filter-count', '');
    wrap.appendChild(el);
  }
  el.textContent = `(${count})`;

  if (btn instanceof HTMLElement && label instanceof HTMLElement) {
    const t = label.textContent?.trim() || '';
    btn.setAttribute('aria-label', `${t} (${count})`);
  }
}

function updateToggleFilterCountBubble(count) {
  const toggleBtn = document.querySelector('.facets-toggle__button');
  if (!(toggleBtn instanceof HTMLButtonElement)) return;

  let wrap = document.getElementById('filter-count-bubble-toggle');
  let textEl = toggleBtn.querySelector('.filter-count-bubble__text');

  if (count <= 0) {
    wrap?.remove();
    return;
  }

  if (!wrap) {
    wrap = document.createElement('div');
    wrap.id = 'filter-count-bubble-toggle';
    wrap.className = 'filter-count-bubble';
    const bg = document.createElement('span');
    bg.className = 'filter-count-bubble__background';
    textEl = document.createElement('span');
    textEl.className = 'filter-count-bubble__text';
    textEl.setAttribute('aria-hidden', 'true');
    wrap.appendChild(bg);
    wrap.appendChild(textEl);
    toggleBtn.appendChild(wrap);
  }

  textEl = wrap.querySelector('.filter-count-bubble__text');
  if (textEl instanceof HTMLElement) textEl.textContent = String(count);
  wrap.setAttribute('aria-label', String(count));
}

/**
 * Mobile deferred drawer: keep tab suffixes + global count badges in sync with DOM (no Apply yet).
 * @param {FacetsFormComponent} facetsForm
 */
function syncDeferredDrawerFilterCountUi(facetsForm) {
  if (!(facetsForm instanceof FacetsFormComponent) || !facetsForm.shouldDeferFilterApply()) return;

  const form = facetsForm.refs.facetsForm;
  if (!(form instanceof HTMLFormElement)) return;

  syncFacetsDrawerMobileMasterDetailTabLabels();

  const count = countPendingFilterSelections(form);
  updateStickyFilterCount(count);
  updateToggleFilterCountBubble(count);
}

/**
 * Mobile #filters-drawer: two-column UI — left tabs from facet labels, right panel shows one facet at a time.
 * Form markup and filter behavior are unchanged; only visibility / which details is open.
 */
function initFacetsDrawerMobileMasterDetail() {
  const mq = window.matchMedia('(max-width: 749px)');
  if (!mq.matches) return;

  const root = document.querySelector('#filters-drawer [data-facets-mobile-drawer-masterdetail]');
  if (!root) return;

  const nav = root.querySelector('.facets-mobile-drawer-masterdetail__nav');
  const slots = Array.from(root.querySelectorAll('.facets-mobile-drawer-masterdetail__panel-slot'));
  if (!(nav instanceof HTMLElement) || slots.length === 0) {
    nav?.setAttribute('hidden', '');
    return;
  }

  nav.innerHTML = '';

  const activate = (index) => {
    slots.forEach((slot, i) => {
      const details = slot.querySelector('details.facets__panel');
      if (details instanceof HTMLDetailsElement) {
        details.open = i === index;
      }
      slot.classList.toggle('facets-mobile-drawer-masterdetail__panel-slot--active', i === index);
      const tab = nav.children[i];
      if (tab instanceof HTMLButtonElement) {
        tab.classList.toggle('is-active', i === index);
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
      }
    });
  };

  slots.forEach((slot, index) => {
    const summary = slot.querySelector('summary.facets__summary');
    const details = slot.querySelector('details.facets__panel');
    if (!(summary instanceof HTMLElement) || !(details instanceof HTMLDetailsElement)) return;

    const labelEl = summary.querySelector('.facets__label');
    const title = labelEl?.textContent?.trim() || `Filter ${index + 1}`;
    let suffix = '';
    const status = summary.querySelector('.facets__status');
    const priceRoot = slot.querySelector('price-facet-component');
    if (priceRoot) {
      suffix = priceFacetMobileMasterDetailTabSuffix(priceRoot);
    } else if (status) {
      const s = status.textContent?.replace(/\s+/g, ' ').trim();
      if (s) suffix = s.startsWith('(') ? ` ${s}` : ` ${s}`;
    }

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'facets-mobile-drawer-masterdetail__tab';
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
    if (!details.id) {
      details.id = `facet-mobile-panel-${root.getAttribute('data-section-id') || 'drawer'}-${index}`;
    }
    btn.setAttribute('aria-controls', details.id);
    const displayTitle = formatFacetDrawerTabLabel(title);
    if (suffix) {
      btn.innerHTML = `${displayTitle} <span class="facets-mobile-drawer-masterdetail__tab-count">${suffix}</span>`;
    } else {
      btn.textContent = displayTitle;
    }
    btn.addEventListener('click', () => activate(index));
    nav.appendChild(btn);
  });

  const navSpacer = document.createElement('div');
  navSpacer.className = 'facets-mobile-drawer-masterdetail__nav-spacer';
  navSpacer.setAttribute('aria-hidden', 'true');
  nav.appendChild(navSpacer);

  nav.removeAttribute('hidden');
  activate(0);

  const drawerDeferForm = document.querySelector(
    '#filters-drawer facets-form-component[data-defer-filter-apply="true"]',
  );
  if (drawerDeferForm instanceof FacetsFormComponent) {
    syncDeferredDrawerFilterCountUi(drawerDeferForm);
  }
}

function scheduleFacetsDrawerMobileMasterDetail() {
  queueMicrotask(() => initFacetsDrawerMobileMasterDetail());
}

document.addEventListener(
  DialogOpenEvent.eventName,
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.id === 'filters-drawer') {
      scheduleFacetsDrawerMobileMasterDetail();
    }
    if (
      (target.id === 'filters-drawer' || target.id === 'sort-sheet-mobile') &&
      isMobileBreakpoint()
    ) {
      plpStickyBarSetSheetOpen(true);
    }
  },
  true
);

document.addEventListener(
  DialogCloseEvent.eventName,
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (
      (target.id === 'filters-drawer' || target.id === 'sort-sheet-mobile') &&
      isMobileBreakpoint()
    ) {
      plpStickyBarSetSheetOpen(false);
    }
  },
  true
);

/**
 * Discard in-drawer facet edits (mobile deferred apply): morph section from current URL so checkboxes match the page.
 */
document.addEventListener(
  DialogCloseEvent.eventName,
  async (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.id !== 'filters-drawer') return;
    if (suppressFiltersDrawerCloseResync) return;
    const formComp = target.querySelector('facets-form-component[data-defer-filter-apply="true"]');
    if (!(formComp instanceof FacetsFormComponent)) return;
    if (!isMobileBreakpoint()) return;
    const sectionId = formComp.sectionId;
    await sectionRenderer.renderSection(sectionId, { url: new URL(window.location.href) });
    appendDeferredProductGridItems();
    scheduleFacetsDrawerMobileMasterDetail();
  },
  true
);

window.matchMedia('(max-width: 749px)').addEventListener('change', scheduleFacetsDrawerMobileMasterDetail);

window.matchMedia('(max-width: 749px)').addEventListener('change', (event) => {
  if (!event.matches) {
    document
      .querySelector('[data-plp-mobile-sticky-actions]')
      ?.classList.remove('plp-mobile-sticky-actions--hidden-on-scroll');
  }
});

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      scheduleFacetsDrawerMobileMasterDetail();
      initPlpMobileStickyBarScrollHide();
    },
    { once: true }
  );
} else {
  scheduleFacetsDrawerMobileMasterDetail();
  initPlpMobileStickyBarScrollHide();
}
